import React, { useCallback, useEffect, useState } from "react";
import { API_ENDPOINTS } from "../../config/api-endpoints";
import { usePut } from "../../packages/hooks";
import { Product } from "../../types";
import { clearDiscountCache, shouldHideDiscount } from "../../utils/discounts";
import Icon from "../icon/icon";
import "./discount-modal.css";

// Discount modal component props interface
export interface DiscountModalProps {
  isOpen: boolean;
  onClose: () => void;
  products: Product[];
  onUpdate: () => Promise<void>;
}

const DiscountModal: React.FC<DiscountModalProps> = ({
  isOpen,
  onClose,
  products,
  onUpdate,
}) => {
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [expiredItems, setExpiredItems] = useState<Product[]>([]);

  // Initialize hooks
  const updateMutation = usePut<Product>(API_ENDPOINTS.PRODUCTS.LIST, {
    onSuccess: () => {
      // Success handled in the batch update function
    },
    onError: () => {
      alert("Ошибка при обновлении каталога");
    },
  });

  const loadExpiredItems = useCallback(async (): Promise<void> => {
    try {
      const expired: Product[] = [];
      for (const product of products) {
        if (product.rental.hasDiscount && shouldHideDiscount(product)) {
          expired.push(product);
        }
      }
      setExpiredItems(expired);
    } catch (error) {
      console.error("Error loading expired items:", error);
    }
  }, [products]);

  useEffect(() => {
    if (isOpen && products && products.length > 0) {
      loadExpiredItems();
    }
  }, [isOpen, products, loadExpiredItems]);

  const handleBatchUpdate = async (): Promise<void> => {
    if (!products || products.length === 0) {
      alert("Нет данных продуктов для обновления");
      return;
    }

    setIsUpdating(true);
    try {
      const expired = products.filter((product) => {
        return product.rental.hasDiscount && shouldHideDiscount(product);
      });

      for (const product of expired) {
        await updateMutation.mutateAsync({
          id: product.id,
          discountAmount: null, // Clear discount amount at product level
          rental: {
            ...product.rental,
            hasDiscount: false,
            discountDuration: 0,
            discountType: "DAYS",
            discountCreatedAt: null,
          },
        });
      }

      clearDiscountCache();
      setLastUpdate(new Date());

      // Refresh the products data
      if (onUpdate) {
        await onUpdate();
      }

      // Reload expired items to reflect changes
      await loadExpiredItems();

      alert(`Обновлено ${expired.length} элементов каталога`);
    } catch (error) {
      alert("Ошибка при обновлении каталога");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleClearCache = (): void => {
    clearDiscountCache();
    setLastUpdate(new Date());
    alert("Кэш скидок очищен");
  };

  if (!isOpen) return null;

  return (
    <div className="discount-modal-overlay" onClick={onClose}>
      <div className="discount-modal" onClick={(e) => e.stopPropagation()}>
        <div className="discount-modal__header">
          <h2>Управление скидками</h2>
          <button className="discount-modal__close" onClick={onClose}>
            <Icon name="close" size="small" />
          </button>
        </div>

        <div className="discount-modal__content">
          <div className="discount-modal__stats">
            <div className="discount-stat">
              <span className="discount-stat__label">Всего товаров:</span>
              <span className="discount-stat__value">{products.length}</span>
            </div>
            <div className="discount-stat">
              <span className="discount-stat__label">Истекших скидок:</span>
              <span className="discount-stat__value discount-stat__value--expired">
                {expiredItems.length}
              </span>
            </div>
          </div>

          {expiredItems.length > 0 && (
            <div className="discount-modal__expired-list">
              <h3>Товары с истекшими скидками:</h3>
              <div className="expired-items">
                {expiredItems.slice(0, 5).map((product) => (
                  <div key={product.id} className="expired-item">
                    <span className="expired-item__title">
                      {product.brand} {product.model}
                    </span>
                    <span className="expired-item__price">
                      {product.rental.price} ₽
                    </span>
                  </div>
                ))}
                {expiredItems.length > 5 && (
                  <div className="expired-item">
                    <span>... и еще {expiredItems.length - 5} товаров</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="discount-modal__actions">
            <button
              className="btn btn--secondary"
              onClick={handleClearCache}
              disabled={isUpdating}
            >
              <Icon name="refresh" size="small" />
              Очистить кэш
            </button>

            {expiredItems.length > 0 && (
              <button
                className="btn btn--primary"
                onClick={handleBatchUpdate}
                disabled={isUpdating}
              >
                <Icon name="update" size="small" />
                {isUpdating
                  ? "Обновление..."
                  : `Обновить ${expiredItems.length} товаров`}
              </button>
            )}
          </div>

          {lastUpdate && (
            <div className="discount-modal__last-update">
              <small>Последнее обновление: {lastUpdate.toLocaleString()}</small>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DiscountModal;
